<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { getLastMessage } from 'dashboard/helper/conversationHelper';

const props = defineProps({
  chat: {
    type: Object,
    required: true
  },
  allKanbanLabels: {
    type: Array,
    required: true
  }
});

import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const lastMessageContent = computed(() => {
  const msg = lastMessageInChat.value;
  if (!msg) return 'Sem mensagens';
  if (msg.content) return msg.content;
  if (msg.attachments && msg.attachments.length > 0) {
    const type = msg.attachments[0].file_type;
    if (type === 'audio') return '🎵 Mensagem de Áudio';
    if (type === 'image') return '📷 Imagem';
    if (type === 'video') return '🎥 Vídeo';
    return '📄 Arquivo Anexado';
  }
  return 'Sem mensagens';
});

const lastMessageIcon = computed(() => {
  const msg = lastMessageInChat.value;
  if (!msg) return '';
  if (msg.message_type === 0) return 'i-lucide-user text-n-slate-10';
  if (msg.message_type === 1) return 'i-lucide-headphones text-n-brand-5';
  if (msg.message_type === 2) return 'i-lucide-lock text-yellow-500';
  return 'i-lucide-message-circle text-n-slate-10';
});

const lastMessageTime = computed(() => {
  const msg = lastMessageInChat.value;
  if (!msg || !msg.created_at) return '';
  const date = new Date(msg.created_at * 1000);
  return date.toLocaleString('pt-BR', { 
    timeZone: 'America/Sao_Paulo', 
    day: '2-digit', month: '2-digit', 
    hour: '2-digit', minute: '2-digit' 
  });
});

const kanbanEnteredAt = computed(() => {
  return props.chat.custom_attributes?.kanban_entered_at || props.chat.last_activity_at;
});

const timeInColumnStr = computed(() => {
  const entered = kanbanEnteredAt.value;
  if (!entered) return '';
  const now = Math.floor(Date.now() / 1000);
  const diffMinutes = Math.max(0, Math.floor((now - entered) / 60));
  if (diffMinutes < 60) return `${diffMinutes}m na etapa`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h na etapa`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d na etapa`;
});

const timeInColumnDate = computed(() => {
  const entered = kanbanEnteredAt.value;
  if (!entered) return '';
  const date = new Date(entered * 1000);
  return date.toLocaleString('pt-BR', { 
    timeZone: 'America/Sao_Paulo', 
    day: '2-digit', month: '2-digit', 
    hour: '2-digit', minute: '2-digit' 
  });
});

const columnTimeStyle = computed(() => {
  const entered = kanbanEnteredAt.value;
  if (!entered) return '';
  const now = Math.floor(Date.now() / 1000);
  const diffHours = (now - entered) / 3600;
  
  if (diffHours > 24) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-400 border border-orange-200 dark:border-orange-800/50';
  if (diffHours > 2) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800/50';
  return 'bg-n-slate-2 text-n-slate-11 border border-n-slate-3';
});



const kanbanLabelsOnChat = computed(() => {
  if (!props.chat.labels) return [];
  return props.allKanbanLabels.filter(l => props.chat.labels.includes(l.title));
});

const hasMultipleKanbanLabels = computed(() => kanbanLabelsOnChat.value.length > 1);

const removeLabel = (labelTitle) => {
  const newLabels = props.chat.labels.filter(l => l !== labelTitle);
  store.dispatch('conversationLabels/update', {
    conversationId: props.chat.id,
    labels: newLabels
  });
};

const archiveFromKanban = () => {
  if(confirm('Remover esta conversa do Kanban? (Ela não será excluída do sistema)')) {
    const kanbanTitles = props.allKanbanLabels.map(l => l.title);
    const newLabels = props.chat.labels.filter(l => !kanbanTitles.includes(l));
    store.dispatch('conversationLabels/update', {
      conversationId: props.chat.id,
      labels: newLabels
    });
  }
};

const openConversation = () => {
  const accountId = store.getters['getCurrentAccountId'];
  router.push(`/app/accounts/${accountId}/conversations/${props.chat.id}`);
};
</script>

<template>
  <div 
    class="flex flex-col bg-n-background p-3 rounded-md shadow-sm cursor-grab active:cursor-grabbing hover:bg-n-slate-2 border transition-colors relative"
    :class="[
      hasMultipleKanbanLabels ? 'border-yellow-400' : 'border-n-slate-4'
    ]"
    @click="openConversation"
  >
    <div v-if="hasMultipleKanbanLabels" class="mb-3 bg-yellow-50 dark:bg-yellow-900/30 p-2 rounded border border-yellow-200 dark:border-yellow-800">
      <div class="flex items-start gap-1 text-yellow-700 dark:text-yellow-500 mb-1">
        <span class="i-lucide-alert-triangle size-4 shrink-0 mt-0.5"></span>
        <span class="text-xs font-medium">Conflito de Etapas Kanban!</span>
      </div>
      <div class="text-[10px] text-yellow-600 dark:text-yellow-400 mb-1.5">Remova as etapas duplicadas para organizar:</div>
      <div class="flex flex-wrap gap-1.5">
        <span 
          v-for="label in kanbanLabelsOnChat" 
          :key="label.id"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 dark:bg-yellow-800/80 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700/50"
        >
          <span class="w-2 h-2 rounded-full" :style="{backgroundColor: label.color}"></span>
          {{ label.title }}
          <button @click.stop="removeLabel(label.title)" class="hover:text-red-500 transition-colors ml-1 p-0.5 bg-n-background/50 rounded flex items-center justify-center" title="Remover etiqueta">
            <span class="i-lucide-x size-3"></span>
          </button>
        </span>
      </div>
    </div>

    <div class="flex items-center gap-2 mb-2">
      <img v-if="chat.meta?.sender?.thumbnail" :src="chat.meta.sender.thumbnail" class="w-6 h-6 rounded-full object-cover border border-n-slate-4" />
      <div v-else class="w-6 h-6 rounded-full bg-n-slate-3 flex items-center justify-center text-xs font-semibold text-n-slate-11">
        {{ chat.meta?.sender?.name?.charAt(0) }}
      </div>
      <h4 class="text-sm font-medium text-n-slate-12 truncate flex-1 m-0">{{ chat.meta?.sender?.name || 'Unknown' }}</h4>
      <button @click.stop="archiveFromKanban" class="text-n-slate-9 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20" title="Remover do Kanban">
        <span class="i-lucide-trash-2 size-3.5"></span>
      </button>
    </div>
    
    <div class="flex items-start gap-1.5 text-xs text-n-slate-11 min-h-[32px] w-full">
      <span v-if="lastMessageIcon" :class="[lastMessageIcon, 'size-3.5 mt-0.5 shrink-0']"></span>
      <p class="line-clamp-2 m-0 flex-1 break-words">
        {{ lastMessageContent }}
      </p>
    </div>
    
    <div class="flex items-end justify-between mt-3 gap-2">
      <div class="flex flex-col gap-1.5 flex-1">
        <div class="flex flex-wrap gap-1" v-if="chat.labels && chat.labels.length">
          <span 
            v-for="labelTitle in chat.labels.filter(l => !kanbanLabelsOnChat.map(k=>k.title).includes(l)).slice(0, 3)" 
            :key="labelTitle"
            class="text-[10px] px-1.5 py-0.5 rounded bg-n-slate-3 text-n-slate-11 border border-n-slate-4"
          >
            {{ labelTitle }}
          </span>
        </div>
        <div class="text-[10px] px-1.5 py-0.5 rounded font-medium w-fit flex items-center gap-1" :class="columnTimeStyle" :title="'Adicionado em: ' + timeInColumnDate">
          <span class="i-lucide-hourglass size-3"></span>
          {{ timeInColumnStr }}
        </div>
      </div>
      <div class="flex flex-col items-end gap-1 shrink-0 text-right">
        <div class="flex items-center gap-1 text-n-slate-9" title="Última mensagem">
          <span class="text-[10px]">{{ lastMessageTime }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
